import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  title: string;
  icon?: LucideIcon;
  /** Texte discret à droite du titre (rôle d'aide). */
  description?: string;
  /** Actions à droite de l'en-tête (boutons, badge d'état). */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Padding du corps (défaut p-5). */
  bodyClassName?: string;
}

/**
 * Carte de section toujours ouverte (non repliable), en-tête plat avec icône.
 * `flex h-full flex-col` : deux SectionCard côte à côte dans une grille
 * `items-stretch` s'alignent à la même hauteur sans casser le contenu.
 */
export function SectionCard({
  title,
  icon: Icon,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: Props) {
  return (
    <Card className={cn("flex h-full flex-col", className)}>
      <div className="flex items-center gap-2 border-b border-border p-5">
        {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {description && !actions && (
          <p className="ml-auto hidden text-xs text-muted sm:block">{description}</p>
        )}
        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>
      <div className={cn("flex-1 p-5", bodyClassName)}>{children}</div>
    </Card>
  );
}
